import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, HeartIcon, CompassIcon, ZapIcon, StickyNoteIcon } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
export function AboutPage() {
  return <main className="w-full bg-cream" data-id="element-79">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 lg:px-8" aria-labelledby="about-heading" data-id="element-80">
        <div className="max-w-4xl mx-auto" data-id="element-81">
          <Badge variant="amber" className="mb-6" data-id="element-82">
            About
          </Badge>
          <h1 id="about-heading" className="font-serif text-5xl md:text-6xl font-bold text-ink mb-8 leading-tight" data-id="element-83">
            Built by a screenwriter who ran out of wall space.
          </h1>
          <div className="w-16 h-0.5 bg-amber mb-8" data-id="element-84"></div>
          <p className="font-sans text-xl text-ink-muted leading-relaxed max-w-2xl" data-id="element-85">
            Slate & Page exists because sticky notes, index cards, and seventeen
            open tabs are not a story development system.
          </p>
        </div>
      </section>

      {/* The Problem We Solved */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="origin-heading" data-id="element-86">
        <div className="max-w-4xl mx-auto" data-id="element-87">
          <div className="grid lg:grid-cols-3 gap-16" data-id="element-88">
            <div className="lg:col-span-2" data-id="element-89">
              <h2 id="origin-heading" className="font-serif text-3xl font-bold text-ink mb-8 leading-tight" data-id="element-90">
                The sticky note problem.
              </h2>

              <div className="space-y-6" data-id="element-91">
                <p className="font-sans text-base text-ink-muted leading-relaxed" data-id="element-92">
                  Every screenwriter knows the feeling. You're deep in a project
                  and your workspace looks like a conspiracy board — sticky
                  notes on the monitor, index cards on the wall, a notebook full
                  of character details you can't quite find, and a spreadsheet
                  that made sense three weeks ago but now just raises questions.
                </p>
                <p className="font-sans text-base text-ink-muted leading-relaxed" data-id="element-93">
                  The information is all there. Somewhere. But finding it — and
                  trusting that it's current — is a different problem entirely.
                  You spend more time managing the mess than writing the story.
                </p>
                <p className="font-sans text-base text-ink-muted leading-relaxed" data-id="element-94">
                  Slate & Page was built to solve that problem. Not by replacing
                  the way you write, but by giving every piece of your story a
                  proper home — somewhere structured, searchable, and connected
                  — so you can stop managing information and start making
                  creative decisions.
                </p>
                <p className="font-sans text-base text-ink-muted leading-relaxed" data-id="element-95">
                  It works alongside whatever you write in. Final Draft, Arc
                  Studio Pro, Highland, a plain text file — Slate & Page doesn't
                  care. It's the development layer: the place where characters,
                  locations, scenes, and world-building live together, linked
                  and accessible, from the first idea to the final draft.
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-96">
              <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm" data-id="element-97">
                <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center mb-3" data-id="element-98">
                  <StickyNoteIcon className="w-5 h-5 text-warm-brown" aria-hidden="true" data-id="element-99" />
                </div>
                <div className="font-serif text-2xl font-bold text-ink mb-1" data-id="element-100">
                  Too many sticky notes
                </div>
                <div className="font-sans text-sm text-ink-muted" data-id="element-101">
                  The original problem. Still the problem we're solving.
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm" data-id="element-102">
                <div className="font-serif text-4xl font-bold text-amber mb-1" data-id="element-103">
                  One place
                </div>
                <div className="font-sans text-sm text-ink-muted" data-id="element-104">
                  For every character, scene, location, and world element
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm" data-id="element-105">
                <div className="font-serif text-4xl font-bold text-amber mb-1" data-id="element-106">
                  Any tool
                </div>
                <div className="font-sans text-sm text-ink-muted" data-id="element-107">
                  Works alongside Final Draft, Arc Studio, Highland, and more
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-navy py-24 px-6 lg:px-8" aria-labelledby="mission-heading" data-id="element-108">
        <div className="max-w-4xl mx-auto" data-id="element-109">
          <h2 id="mission-heading" className="font-serif text-3xl md:text-4xl font-bold text-white mb-16 text-center" data-id="element-110">
            What we believe.
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-111">
            <div className="text-center" data-id="element-112">
              <div className="w-14 h-14 bg-amber/20 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-113">
                <HeartIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-114" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-white mb-3" data-id="element-115">
                Great stories are built, not just written.
              </h3>
              <p className="font-sans text-sm text-white/60 leading-relaxed" data-id="element-116">
                The best screenplays come from deep development work —
                understanding your characters, your world, and your structure
                before you write a single page. That work deserves a proper
                home.
              </p>
            </div>

            <div className="text-center" data-id="element-117">
              <div className="w-14 h-14 bg-amber/20 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-118">
                <CompassIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-119" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-white mb-3" data-id="element-120">
                Tools should respect the complexity of craft.
              </h3>
              <p className="font-sans text-sm text-white/60 leading-relaxed" data-id="element-121">
                Screenwriting is not a manufacturing process. Writers don't need
                productivity software — they need thinking tools. Tools that
                understand story structure, character psychology, and narrative
                logic.
              </p>
            </div>

            <div className="text-center" data-id="element-122">
              <div className="w-14 h-14 bg-amber/20 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-123">
                <ZapIcon className="w-7 h-7 text-amber" aria-hidden="true" data-id="element-124" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-white mb-3" data-id="element-125">
                Your creative energy is precious.
              </h3>
              <p className="font-sans text-sm text-white/60 leading-relaxed" data-id="element-126">
                Every minute spent managing information is a minute not spent
                writing. We're here to take the logistics off your plate so you
                can put your energy where it belongs: the story.
              </p>
            </div>
          </div>

          <div className="mt-16 max-w-2xl mx-auto text-center" data-id="element-127">
            <blockquote className="font-serif text-2xl md:text-3xl italic text-white/90 leading-relaxed" data-id="element-128">
              "Writers deserve tools that respect the complexity of their craft
              — and trust them to know what their story needs."
            </blockquote>
          </div>
        </div>
      </section>

      {/* What's Next / Roadmap */}
      <section className="bg-cream py-24 px-6 lg:px-8" aria-labelledby="roadmap-heading" data-id="element-129">
        <div className="max-w-4xl mx-auto" data-id="element-130">
          <div className="mb-16" data-id="element-131">
            <h2 id="roadmap-heading" className="font-serif text-3xl md:text-4xl font-bold text-ink mb-4" data-id="element-132">
              What we're building next.
            </h2>
            <p className="font-sans text-base text-ink-muted leading-relaxed max-w-xl" data-id="element-133">
              Slate & Page is actively developed. Here's what's coming — and
              we'd love your input on what matters most.
            </p>
          </div>

          <div className="space-y-4" data-id="element-134">
            <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm flex items-start gap-5" data-id="element-135">
              <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-136">
                <span className="font-sans text-base" data-id="element-137">🤝</span>
              </div>
              <div data-id="element-138">
                <div className="flex items-center gap-3 mb-1" data-id="element-139">
                  <h3 className="font-serif text-lg font-semibold text-ink" data-id="element-140">
                    Collaboration & Sharing
                  </h3>
                  <Badge variant="amber" data-id="element-141">Coming Soon</Badge>
                </div>
                <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-142">
                  Real-time collaboration for writing rooms and co-writers.
                  Share projects with producers and directors. Commenting,
                  @mentions, and version history.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm flex items-start gap-5" data-id="element-143">
              <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-144">
                <span className="font-sans text-base" data-id="element-145">🧠</span>
              </div>
              <div data-id="element-146">
                <div className="flex items-center gap-3 mb-1" data-id="element-147">
                  <h3 className="font-serif text-lg font-semibold text-ink" data-id="element-148">
                    AI Continuity Assistant
                  </h3>
                  <Badge variant="amber" data-id="element-149">In Development</Badge>
                </div>
                <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-150">
                  An AI layer that reads your story elements and flags potential
                  continuity issues, suggests connections you might have missed,
                  and helps you think through character motivations.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm flex items-start gap-5" data-id="element-151">
              <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-152">
                <span className="font-sans text-base" data-id="element-153">📱</span>
              </div>
              <div data-id="element-154">
                <div className="flex items-center gap-3 mb-1" data-id="element-155">
                  <h3 className="font-serif text-lg font-semibold text-ink" data-id="element-156">
                    Mobile App
                  </h3>
                  <Badge variant="parchment" data-id="element-157">Planned</Badge>
                </div>
                <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-158">
                  A native iOS and Android app for capturing ideas, reviewing
                  character notes, and checking continuity on the go. Because
                  inspiration doesn't wait for you to be at your desk.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-parchment shadow-sm flex items-start gap-5" data-id="element-159">
              <div className="w-10 h-10 bg-amber-light rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-160">
                <span className="font-sans text-base" data-id="element-161">🔗</span>
              </div>
              <div data-id="element-162">
                <div className="flex items-center gap-3 mb-1" data-id="element-163">
                  <h3 className="font-serif text-lg font-semibold text-ink" data-id="element-164">
                    Script Editor Integration
                  </h3>
                  <Badge variant="parchment" data-id="element-165">Planned</Badge>
                </div>
                <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-166">
                  Direct integration with Final Draft, Arc Studio Pro, and
                  Highland. Import your script to automatically populate scene
                  data, or export your Slate & Page structure as a script
                  outline.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-parchment rounded-2xl p-8 border border-parchment/80 text-center" data-id="element-167">
            <h3 className="font-serif text-xl font-semibold text-ink mb-3" data-id="element-168">
              Have a feature request?
            </h3>
            <p className="font-sans text-sm text-ink-muted mb-5" data-id="element-169">
              We read every message. Early Access users have direct input on the
              roadmap.
            </p>
            <a href="mailto:hello@slateandpage.com" className="inline-block font-sans text-sm font-medium bg-amber hover:bg-amber-dark text-white px-6 py-3 rounded-full transition-colors" data-id="element-170">
              Send us a note
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="about-cta-heading" data-id="element-171">
        <div className="max-w-3xl mx-auto text-center" data-id="element-172">
          <h2 id="about-cta-heading" className="font-serif text-4xl font-bold text-ink mb-6" data-id="element-173">
            Join us while we're still building.
          </h2>
          <p className="font-sans text-lg text-ink-muted mb-10 max-w-xl mx-auto" data-id="element-174">
            Early Access users get first access to new features, direct input on
            the roadmap, and a locked-in price when we launch paid plans.
          </p>
          <Link to="/waitlist" className="inline-block font-sans font-medium text-base bg-amber hover:bg-amber-dark text-white px-10 py-4 rounded-full transition-colors shadow-md" data-id="element-175">
            Join the Waitlist
          </Link>
        </div>
      </section>
    </main>;
}